import { useQuery } from '@tanstack/react-query';
import { blogService, WordPressPost, WordPressCategory, WordPressAuthor } from '@/services/blogService';
import { supabaseBlogService, SupabaseBlogPost } from '@/services/supabaseBlogService';
import { USE_SUPABASE_CMS } from '@/config/cms';

export const useBlogPosts = (params: {
  page?: number;
  per_page?: number;
  search?: string;
  categories?: string;
  orderby?: string;
  order?: 'asc' | 'desc';
  bust_cache?: boolean;
} = {}) => {
  return useQuery({
    queryKey: ['blog-posts', params, USE_SUPABASE_CMS],
    queryFn: async () => {
      if (USE_SUPABASE_CMS) {
        const response = await supabaseBlogService.fetchPosts(params);
        return {
          posts: response.posts.map(transformSupabasePost),
          totalPages: Math.ceil(response.totalCount / (params.per_page || 12)),
          total: response.totalCount
        };
      } else {
        return blogService.fetchPosts(params);
      }
    },
    staleTime: params.bust_cache ? 0 : 15 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

export const useBlogPost = (slug: string, bustCache = false) => {
  return useQuery({
    queryKey: ['blog-post', slug, bustCache, USE_SUPABASE_CMS],
    queryFn: async () => {
      if (USE_SUPABASE_CMS) {
        const post = await supabaseBlogService.fetchPost(slug);
        return post ? transformSupabasePost(post) : null;
      } else {
        return blogService.fetchPost(slug, bustCache);
      }
    },
    enabled: !!slug,
    staleTime: bustCache ? 0 : 15 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

export const useBlogCategories = (bustCache = false) => {
  return useQuery({
    queryKey: ['blog-categories', bustCache, USE_SUPABASE_CMS],
    queryFn: async () => {
      if (USE_SUPABASE_CMS) {
        const categories = await supabaseBlogService.fetchCategories();
        return categories.map((cat, idx) => ({
          id: idx + 1,
          name: cat.name,
          count: cat.count,
          slug: cat.name.toLowerCase().replace(/\s+/g, '-'),
          description: '',
          link: `/blog?category=${cat.name}`,
          taxonomy: 'category',
          parent: 0,
          meta: []
        }));
      } else {
        return blogService.fetchCategories(bustCache);
      }
    },
    staleTime: bustCache ? 0 : 60 * 60 * 1000,
    gcTime: 2 * 60 * 60 * 1000,
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
  });
};

export const useBlogAuthor = (id: number) => {
  return useQuery({
    queryKey: ['blog-author', id, USE_SUPABASE_CMS],
    queryFn: () => {
      if (USE_SUPABASE_CMS || !id) {
        return null;
      } else {
        return blogService.fetchAuthor(id);
      }
    },
    enabled: !!id && !USE_SUPABASE_CMS,
    staleTime: 15 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
  });
};

export const useBlogFeaturedImage = (post: WordPressPost | undefined) => {
  return useQuery({
    queryKey: ['blog-featured-image', post?.slug || post?.featured_media, USE_SUPABASE_CMS],
    queryFn: () => {
      if (!post) return null;
      
      if (USE_SUPABASE_CMS) {
        return (post as any).featured_image_url || null;
      } else {
        return blogService.getFeaturedImageUrl(post);
      }
    },
    enabled: !!post && (!!post?.featured_media || !!(post as any)?.featured_image_url),
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
  });
};

export const useBlogComments = (postId: number) => {
  return useQuery({
    queryKey: ['blog-comments', postId],
    queryFn: () => blogService.fetchComments(postId),
    enabled: !!postId && !USE_SUPABASE_CMS,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
};

// Transform Supabase post to match WordPress interface
export function transformSupabasePost(post: SupabaseBlogPost): WordPressPost {
  return {
    id: parseInt(post.id.replace(/-/g, '').substring(0, 8), 16),
    date: post.published_at || post.created_at,
    modified: post.updated_at,
    slug: post.slug,
    title: {
      rendered: post.title
    },
    content: {
      rendered: post.content
    },
    excerpt: {
      rendered: post.excerpt || ''
    },
    author: 0,
    featured_media: post.featured_image_url ? 1 : 0,
    categories: post.categories.map((_, idx) => idx + 1),
    tags: post.tags.map((_, idx) => idx + 1),
    _links: {
      self: [{ href: '' }],
      author: [{ href: '' }],
      'wp:featuredmedia': [{ href: '' }],
      'wp:attachment': [{ href: '' }],
      'wp:term': [{ taxonomy: 'category', href: '' }]
    },
    featured_image_url: post.featured_image_url
  } as any;
}
